import { Injectable } from '@angular/core';
import { ProductListApiService } from '@epicuro-next/data/engagement/engagement-api';
import {
  ITopList,
  ReportingApiService,
} from '@epicuro-next/data/reporting-api';
import { IUser, UsersApiService } from '@epicuro-next/data/users';
import { BasketItemSource } from '@epicuro-next/models/basket';
import {
  selectBestPerformingDashboard,
  selectListExchange,
} from '@epicuro-next/state/analytics-entities';
import { setProductSource } from '@epicuro-next/state/config-strip-state';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { selectUserExternalId } from '@epicuro-next/state/user-state';
import { arrayIsNotEmpty, isNonNullish } from '@epicuro-next/utilities';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { forkJoin, Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';

import {
  BestPerformingListQuery,
  BestPerformingListsState,
} from '../../models';
import { mapBestPerformingLists, mapTopListsToOwners } from '../../utils';

const initialState: BestPerformingListsState = {
  bestPerformingLists: [],
  listsOnQuery: [],
  filters: null,
  params: null,
  loading: true,
  hasError: false,
};

@Injectable()
export class BestPerformingListsStore extends ComponentStore<BestPerformingListsState> {
  public listsOnQuery$ = this.select(({ listsOnQuery }) => listsOnQuery).pipe(
    filter(isNonNullish),
  );
  public bestPerformingLists$ = this.select(
    ({ bestPerformingLists }) => bestPerformingLists,
  ).pipe(map((lists) => lists.filter((list) => isNonNullish(list.listInfo))));
  public hasError$ = this.select(({ hasError }) => hasError);
  public loading$ = this.select(({ loading }) => loading);
  private params$ = this.select(({ params }) => params);
  public filters$ = this.select(({ filters }) => filters);
  public brandOrgFilter$ = this.select(
    this.filters$,
    (filters) => filters?.brandOrganisations,
  );

  public selectedBrandOrgIds$ = this.select(
    this.params$,
    (params) => params?.brandOrgIds ?? [],
  );

  public analyticsListExchange$ = this.store.pipe(
    select(selectListExchange),
    filter(isNonNullish),
  );

  public currentUserExternalId$ = this.store.pipe(
    select(selectUserExternalId),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public brandOrgsFilterEnabled$ = this.store
    .select(selectBestPerformingDashboard)
    .pipe(
      map((bestPerformingDashboardSettings) =>
        Boolean(
          bestPerformingDashboardSettings.dashboards?.lists?.brandOrgFilter
            ?.enabled,
        ),
      ),
    );

  public getTopListFilters$ = this.effect(
    (isEnabled$: Observable<boolean | undefined>) =>
      isEnabled$.pipe(
        filter(Boolean),
        switchMap(() =>
          this.reportingService.getTopListFilters().pipe(
            tapResponse(
              (filters) => {
                this.patchState({ filters });
              },
              () => {
                this.store.dispatch(
                  showSnackBarMessage({
                    message: $localize`:AnalyticsDashboard|Failed get dashboard filters @@failedToGetDashboardFilters:Failed to get filters`,
                    messageType: MessageType.DANGER,
                  }),
                );
              },
            ),
          ),
        ),
      ),
  )(this.brandOrgsFilterEnabled$);

  public readonly loadTopLists = this.effect(
    (params$: Observable<BestPerformingListQuery | null>) =>
      params$.pipe(
        filter(isNonNullish),
        switchMap(({ topN, dateRange = { from: '', to: '' }, brandOrgIds }) =>
          this.reportingService
            .getTopListRevenues(topN ?? 0, dateRange, brandOrgIds)
            .pipe(
              tapResponse(
                (topLists) => {
                  if (topLists.length === 0)
                    this.resetSetState({ loading: false });
                  else
                    this.patchState({
                      listsOnQuery: topLists,
                      hasError: false,
                    });
                },
                () => this.resetSetState({ loading: false, hasError: true }),
              ),
            ),
        ),
      ),
  )(this.params$);

  public readonly loadBestPerformingLists = this.effect(
    (listsOnQuery$: Observable<ITopList[]>) =>
      listsOnQuery$.pipe(
        filter(arrayIsNotEmpty),
        switchMap((topLists) => {
          const productListIds = topLists.map(({ listId }) => listId);
          const externalUserIds = topLists.map(({ ownerId }) => ownerId ?? '');

          return forkJoin([
            this.productListService.getListsByIds(productListIds),
            this.usersService
              .getUsersByExternalId(externalUserIds)
              .pipe(
                map((owners: IUser[]) => mapTopListsToOwners(topLists, owners)),
              ),
          ]).pipe(
            map(([lists, topListsWithOwners]) =>
              mapBestPerformingLists(lists, topListsWithOwners),
            ),
            tapResponse(
              (data) =>
                this.patchState({
                  bestPerformingLists: data,
                  loading: false,
                  hasError: false,
                }),
              () => this.resetSetState({ loading: false, hasError: true }),
            ),
          );
        }),
      ),
  )(this.listsOnQuery$);

  constructor(
    private store: Store,
    private productListService: ProductListApiService,
    private usersService: UsersApiService,
    private reportingService: ReportingApiService,
  ) {
    super(initialState);
  }

  public setParams(payload: Partial<BestPerformingListQuery>) {
    this.patchState((state) => ({
      loading: true,
      params: {
        topN: 10,
        ...state.params,
        ...payload,
      },
    }));
  }

  public resetSetState({
    loading,
    hasError = false,
  }: {
    loading: boolean;
    hasError?: boolean;
  }) {
    this.patchState({
      bestPerformingLists: [],
      listsOnQuery: [],
      loading,
      hasError,
    });
  }

  public setSource(listId: string) {
    this.store.dispatch(
      setProductSource({
        source: BasketItemSource.LIST_DASHBOARD,
        details: { listId: [listId] },
      }),
    );
  }
}
